/**
 * 個別説明「候補日の確定/拒否」ポップアップ。2026-07-23 追加。
 *
 * フォーム送信時に顧客が選択した候補日 (Deal.briefingCandidates) を自動紐付けで
 * 一覧表示し、各候補に「確定」「拒否」ボタンを設置する。
 *   - 確定: confirmBriefingDate → 枠予約 + 他候補の自動キャンセル + 顧客へ確定メール
 *   - 拒否: declineBriefingCandidate → その候補のみ declined (枠は消費しない)
 * 商談詳細タブ / 個別説明ページ (顧客・SAポータル共用) から呼ばれる。
 *
 * 候補/セッションの読み出しヘルパー (dealBriefingCandidates 等) は 2026-09-09 に
 * `@goenhub/shared` (utils/briefing-candidates.ts) へ移設した (functions と共用)。
 */
import type { BriefingCandidateEntry } from '@goenhub/shared';
import {
  BRIEFING_DELETED_SLOT_LABEL,
  BRIEFING_STYLE_LABEL,
  resolveBriefingStyle,
} from '@goenhub/shared';
import { Check, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useBriefingSchedules,
  useConfirmBriefingDate,
  useDeclineBriefingCandidate,
  useDeletedBriefingScheduleIds,
} from '@/hooks/useBriefings';

/** YYYY-MM-DD → 「yyyy年m月d日 (曜)」 */
function formatJpFullDate(ymd: string): string {
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const days = ['日', '月', '火', '水', '木', '金', '土'];
  return `${Number(m[1])}年${Number(m[2])}月${Number(m[3])}日 (${days[d.getDay()]})`;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string | null;
  /** 表示する候補 (pending のみ渡す)。 */
  candidates: BriefingCandidateEntry[];
}

export function BriefingCandidateDecisionDialog({ open, onOpenChange, dealId, candidates }: Props) {
  const { data: schedules, isLoading } = useBriefingSchedules();
  // 削除済みの枠は id の代わりに「削除された候補日」と表記する
  const deletedScheduleIds = useDeletedBriefingScheduleIds(candidates.map((c) => c.scheduleId));
  const confirmMutation = useConfirmBriefingDate();
  const declineMutation = useDeclineBriefingCandidate();
  // 拒否済み候補をローカルで隠す (親の再フェッチ完了前のちらつき防止)
  const [decidedIds, setDecidedIds] = useState<string[]>([]);

  const visible = candidates.filter((c) => !decidedIds.includes(c.scheduleId));

  const handleConfirm = async (candidate: BriefingCandidateEntry) => {
    if (!dealId) return;
    try {
      await confirmMutation.mutateAsync({
        dealId,
        briefingScheduleId: candidate.scheduleId,
        // 「両方可」枠は顧客が選んだスタイルで確定する
        ...(candidate.selectedStyle ? { selectedStyle: candidate.selectedStyle } : {}),
      });
      toast.success(
        '日程を確定しました。他の候補日は自動キャンセルされ、顧客に確定メールを送信します。',
      );
      onOpenChange(false);
      setDecidedIds([]);
    } catch (err) {
      toast.error(`確定失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDecline = async (candidate: BriefingCandidateEntry) => {
    if (!dealId) return;
    const scheduleId = candidate.scheduleId;
    try {
      await declineMutation.mutateAsync({
        dealId,
        briefingScheduleId: scheduleId,
        formSubmissionId: candidate.formSubmissionId ?? null,
      });
      toast.success('候補日を拒否しました');
      const next = [...decidedIds, scheduleId];
      setDecidedIds(next);
      // 全候補を処理し終えたら閉じる
      if (candidates.every((c) => next.includes(c.scheduleId))) {
        onOpenChange(false);
        setDecidedIds([]);
      }
    } catch (err) {
      toast.error(`拒否失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const busy = confirmMutation.isPending || declineMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>候補日を確定しますか？</DialogTitle>
          <DialogDescription>
            「確定」でその候補日に日程を確定します（他の候補日は自動キャンセルされ、顧客に確定メールが送信されます）。「拒否」はその候補日のみ取り下げます。
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <Spinner />
        ) : visible.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] py-4">処理できる候補日がありません。</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {visible.map((c) => {
              const s = schedules?.find((x) => x.id === c.scheduleId);
              if (!s) {
                return (
                  <div
                    key={c.scheduleId}
                    className="rounded-md border border-[var(--border)] px-3 py-2 text-xs text-[var(--text-muted)]"
                  >
                    {deletedScheduleIds.has(c.scheduleId) ? (
                      BRIEFING_DELETED_SLOT_LABEL
                    ) : (
                      <>
                        枠が見つかりません (削除済みの可能性):{' '}
                        <span className="font-mono">{c.scheduleId}</span>
                      </>
                    )}
                  </div>
                );
              }
              // 1枠=1予約: 予約済み (bookedCount>0) はクローズ。
              // capacity = 同時参加可能人数 (1組の人数上限)。
              const seats = Math.max(1, c.participantCount ?? 1);
              // 「両方可」枠は顧客がフォームで選んだスタイルを表示する
              const style = resolveBriefingStyle(s.style, c.selectedStyle);
              const alreadyBooked = (s.bookedCount ?? 0) > 0;
              const overCapacity = seats > s.capacity;
              const full = alreadyBooked || overCapacity || !s.isActive;
              return (
                <div
                  key={c.scheduleId}
                  className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2.5 flex items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[var(--text-primary)] tabular-nums">
                      {formatJpFullDate(s.date)} {s.startTime}〜{s.endTime}
                    </div>
                    <div className="text-xs text-[var(--text-muted)] mt-0.5">
                      {BRIEFING_STYLE_LABEL[style] ?? style}
                      {style !== 'online' && s.location ? ` @ ${s.location}` : ''}
                      {' ・ 参加'}
                      {seats}名{' ・ 受入'}
                      {s.capacity}名
                      {full && (
                        <>
                          {' ・ '}
                          <span className="text-[var(--accent-red)]">
                            {!s.isActive
                              ? '受付停止中'
                              : alreadyBooked
                                ? '予約済み'
                                : '受入人数超過'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={busy || full}
                    onClick={() => handleConfirm(c)}
                    title={full ? 'この枠は受付できません' : 'この候補日で確定'}
                  >
                    <Check className="size-3.5" />
                    確定
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => handleDecline(c)}
                    title="この候補日を拒否"
                  >
                    <X className="size-3.5" />
                    拒否
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
